import React, { useEffect, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import styled from 'styled-components'
import { Checkbox } from '@mui/material'

import { useTableData } from '@/hooks/useTableData'
import { getIdListFiltered } from '@/utils/index'
import { normalizeClassName } from '@/utils/helpers'
import { toast } from 'react-toastify'

import CustomView from '../CustomView'
import Actions from './Actions'

const CustomRow = styled(CustomView)`
  && {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flow-root;
    min-width: 100px;
    max-width: 180px;
    padding-top: 16px;
    align-items: center;
    padding-bottom: 16px;
    font-size: 14px;
    margin-left: 8px;
    text-align: center;
    &.row-inativo {
      color: #f64a14;
    }
  }
`

interface ITableRow {
  data: any
  action: string[]
  columnsId: number[]
  showSelectColumn?: boolean
  handleAction?: (action: string, rowData?: any) => void
  overrideAction?: boolean
  allowEditInactive?: boolean
  index: number
  canChoiceOnlyOneSelect?: boolean
  selectionMatchFields?: string[]
  selectionConstraintMessage?: string
  selectDisabled?: boolean
  openActionsUpward?: boolean
}

function TableRow({
  data = {},
  action,
  columnsId,
  showSelectColumn,
  handleAction,
  overrideAction,
  allowEditInactive,
  canChoiceOnlyOneSelect,
  selectionMatchFields,
  selectionConstraintMessage,
  index,
  selectDisabled,
  openActionsUpward
}: ITableRow) {
  const [row, setRow] = useState<any>([])
  const { CHECK_SELECTS, SET_CHECK_SELECTS } = useTableData()

  const matchesSelectionRules = (row: any) => {
    if (!firstSelectedRow || !selectionMatchFields?.length) return true

    return selectionMatchFields.every(
      (field) => firstSelectedRow[field] === row[field]
    )
  }

  const firstSelectedRow = CHECK_SELECTS.find((item: any) => item.checked)?.row
  const isSelectionIncompatible = !matchesSelectionRules(data)

  useEffect(() => {
    getRowData()
  }, [data])

  function getRowData() {
    let arr: string[] = []

    Object.keys(data).forEach((key) => {
      if (key !== 'id') {
        arr.push(data[key])
      }
    })
    setRow(arr)
  }

  function handleSelect(e: any) {
    if (!e.target.checked) {
      CHECK_SELECTS[index] = { checked: false, id: '', row: null }
      SET_CHECK_SELECTS([...CHECK_SELECTS])
      return
    }

    const isFirstSelection = !firstSelectedRow
    const hasSelectionRules = Boolean(selectionMatchFields?.length)

    if (isFirstSelection && hasSelectionRules) {
      toast.warn(
        selectionConstraintMessage ||
          'Para seleção em lote, escolha apenas registros com campos correspondentes.'
      )
    }

    const matchesSelection = matchesSelectionRules(data)

    if (firstSelectedRow && !matchesSelection) return

    if (
      canChoiceOnlyOneSelect &&
      getIdListFiltered(CHECK_SELECTS).length === 1
    ) {
      const resetChecks = CHECK_SELECTS.map((x: any) => ({
        ...x,
        checked: false
      }))

      resetChecks[index] = {
        checked: true,
        id: data.id,
        row: data
      }
      return SET_CHECK_SELECTS(resetChecks)
    }

    CHECK_SELECTS[index] = { checked: true, id: data.id, row: data }
    SET_CHECK_SELECTS([...CHECK_SELECTS])
  }

  return (
    <CustomView flexDirection="row" gap={1}>
      {showSelectColumn && (
        <Checkbox
          sx={{ marginLeft: '24px' }}
          onChange={handleSelect}
          checked={CHECK_SELECTS[index]?.checked ?? false}
          disabled={
            !CHECK_SELECTS[index]?.checked &&
            (selectDisabled || isSelectionIncompatible)
          }
        />
      )}

      {action && !!action?.length && (
        <CustomRow style={{ display: 'flex', alignItems: 'center' }}>
          <Actions
            actions={action}
            data={data}
            handleAction={handleAction}
            overrideAction={overrideAction}
            allowEditInactive={allowEditInactive}
            openUpward={openActionsUpward}
          />
        </CustomRow>
      )}

      {row.map((item: any, index: number) => (
        <React.Fragment key={index}>
          {columnsId.includes(index) && (
            <Tooltip
              arrow
              placement="right-start"
              title={item?.length >= 17 ? item : ''}
              key={index}
              componentsProps={{
                tooltip: {
                  style: {
                    fontSize: '14px'
                  }
                }
              }}
            >
              <CustomRow className={`row-${normalizeClassName(item)}`}>
                {item}
              </CustomRow>
            </Tooltip>
          )}
        </React.Fragment>
      ))}
    </CustomView>
  )
}

export default TableRow
